import React, { useState, useEffect, useMemo } from "react";
import { message } from "antd";
import {
  getFavoriteFaculty,
  getFavoriteUnit,
  getFavoriteInstitution,
  getFavoriteVenue,
  deleteFavoriteItem,
} from "Explore/Services/Favorite";
import { exportSavedResultsCSV, exportSavedResultsXLSX } from "../../utils/exportSavedResults";
import FavoritesToolbar from "./FavoritesToolbar";
import FavoritesListView from "./FavoritesListView";
import FavoritesGridView from "./FavoritesGridView";
import FavoritesEmptyState from "./FavoritesEmptyState";

const FavoritesPanel = ({ activeTab }) => {
  // activeTab is one of: 'fav-researchers', 'fav-units', 'fav-institutions', 'fav-venues'
  const entityType = useMemo(() => {
    if (activeTab === "fav-units") return "units";
    if (activeTab === "fav-institutions") return "institutions";
    if (activeTab === "fav-venues") return "venues";
    return "researchers";
  }, [activeTab]);

  const title = useMemo(() => {
    if (entityType === "units") return "Favorite Units";
    if (entityType === "institutions") return "Favorite Institutions";
    if (entityType === "venues") return "Favorite Venues";
    return "Favorite Researchers";
  }, [entityType]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const loadData = async () => {
    setLoading(true);
    try {
      let res = null;
      if (entityType === "units") {
        res = await getFavoriteUnit({ page: 0, rowsPerPage: 1000 });
      } else if (entityType === "institutions") {
        res = await getFavoriteInstitution({ page: 0, rowsPerPage: 1000 });
      } else if (entityType === "venues") {
        res = await getFavoriteVenue({ page: 0, rowsPerPage: 1000 });
      } else {
        res = await getFavoriteFaculty({ page: 0, rowsPerPage: 1000 });
      }

      const results = res?.results || (Array.isArray(res) ? res : []);
      setItems(results);
    } catch (err) {
      console.error("Failed to load favorites", err);
      message.error("Failed to load favorites.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    setSearchTerm("");
  }, [entityType]);

  const handleRemove = async (itemId) => {
    try {
      await deleteFavoriteItem({ itemId });
      setItems((prev) => prev.filter((item) => item.id !== itemId));
      message.success("Removed from favorites.");
    } catch (err) {
      console.error("Failed to remove favorite", err);
      message.error("Failed to remove item.");
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const q = searchTerm.toLowerCase();
    return items.filter((item) => {
      const name = (item.name || "").toLowerCase();
      const inst = (item.institution_name || "").toLowerCase();
      const pos = (item.position || "").toLowerCase();
      const city = (item.city || "").toLowerCase();
      return name.includes(q) || inst.includes(q) || pos.includes(q) || city.includes(q);
    });
  }, [items, searchTerm]);

  const handleExportCSV = () => {
    exportSavedResultsCSV({
      name: `${title}_export`,
      entityType,
      items: filteredItems,
    });
    message.success("CSV download started.");
  };

  const handleExportExcel = async () => {
    try {
      await exportSavedResultsXLSX({
        name: `${title}_export`,
        entityType,
        items: filteredItems,
      });
      message.success("Excel download started.");
    } catch (e) {
      console.error(e);
      message.error("Failed to generate Excel file.");
    }
  };

  return (
    <div className="flex flex-col w-full pb-10">
      <FavoritesToolbar
        title={title}
        totalCount={items.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onExportCSV={handleExportCSV}
        onExportExcel={handleExportExcel}
      />


      {!loading && items.length === 0 ? (
        <FavoritesEmptyState entityType={entityType} />
      ) : !loading && filteredItems.length === 0 ? (
        <div className="py-16 text-center text-slate-500 bg-white rounded-xl border border-slate-200 shadow-xs">
          <p className="text-sm">No favorites matching "{searchTerm}"</p>
        </div>
      ) : viewMode === "list" ? (
        <FavoritesListView
          items={filteredItems}
          loading={loading}
          entityType={entityType}
          onRemove={handleRemove}
        />
      ) : (
        <FavoritesGridView
          items={filteredItems}
          loading={loading}
          entityType={entityType}
          onRemove={handleRemove}
        />
      )}
    </div>
  );
};

export default FavoritesPanel;
